/**
 * Date/time formatting helpers — used across the CRM for human-readable
 * timestamps.
 *
 * Conventions (decided May 2026):
 *   Date    →  "23 Jun, 2026"
 *   Time    →  "10:30 AM" / "5:00 PM"
 *   Joined  →  "23 Jun, 2026 · 10:30 AM"
 *
 * All helpers accept either an ISO string or a Date. Empty/invalid input
 * returns a soft em-dash "—" so the UI never shows "Invalid Date".
 */

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function _toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  const d = new Date(value);
  return isNaN(d) ? null : d;
}

export function formatDate(value) {
  const d = _toDate(value);
  if (!d) return "—";
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]}, ${d.getFullYear()}`;
}

export function formatTime(value) {
  const d = _toDate(value);
  if (!d) return "—";
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${String(minutes).padStart(2, "0")} ${ampm}`;
}

export function formatDateTime(value) {
  const d = _toDate(value);
  if (!d) return "—";
  return `${formatDate(d)} · ${formatTime(d)}`;
}

/**
 * Build a value suitable for an `<input type="datetime-local">`
 * from an ISO string. The HTML control expects "YYYY-MM-DDTHH:mm"
 * in *local* time (no Z, no offset).
 */
export function toDateTimeLocalInput(value) {
  const d = _toDate(value);
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Convert a datetime-local input value back to an ISO 8601 string with
 * the local TZ offset preserved. The backend's _resolve_date_range and
 * the Razorpay webhook parsers both accept this form.
 */
export function dateTimeLocalToIso(local) {
  if (!local) return "";
  const d = new Date(local);
  if (isNaN(d)) return "";
  return d.toISOString();
}
